<template lang="pug">
  mixin yearOptions()
    option(value='') Select Academic Year
    option(v-for='year in yearParameters', :value='year.value') {{ year.label }}

  mixin campusOptions()
    option(value='') Select a Campus
    option(v-for='campus in filteredCampusParameters', :value='campus.value') {{ campus.label }}

  mixin pagination()
    .col {{ numRecordsFound }}
    .col-auto.align-self-end
      nav
        ul.pagination.my-0
          li.page-item
            a.page-link(aria-label='Previous', @click='goToPage(currentPage - 1)')
              span(aria-hidden='true')
                font-awesome-icon(:icon='["fas", "chevron-left"]')
              span.sr-only Previous

          li.page-item(v-for='pageNum in pagesAvailable', :class='pageNum === currentPage ? "active" : ""')
            a.page-link(@click='goToPage(pageNum)') {{ pageNum }}

          li.page-item
            a.page-link(aria-label="Next", @click='goToPage(currentPage + 1)')
              span(aria-hidden='true')
                font-awesome-icon(:icon='["fas", "chevron-right"]')
              span.sr-only Next

  .container-fluid.list-top-padding(v-if='canAccessWatTemplates', :class='sessionGetDisplayClass(["body", "content"])')
    .page-content
      DueDateBanner(v-if='activeWatDates.length > 0', :dates='activeWatDates')
      .row
        .col-8
          h3 WORK ASSIGNMENTS
      .row
        CoachmarkTip.col-auto(:options='getCoachmarkData("0")')
          .row
            .col-auto
              select.custom-select(v-model='selectedYearParameter', @change='filterChange')
                +yearOptions
            .col-auto
              select.custom-select(v-model='selectedCampusParameter', @change='filterChange')
                +campusOptions
            .col-auto
              button.btn.btn-primary.test(type='button', :disabled='!canSearch', @click='doSearch') Search
            .col-auto
              a.btn.btn-success(v-if='exportDownloadLink', :href='exportDownloadLink')
                font-awesome-icon.mr-2(:icon='["fas", "cloud-download-alt"]', size='lg')
                | Download
              button.btn.btn-success.px-3(v-else, type='button', :disabled='!canGenerateExcel', @click='generateExcelFile') Export Data
                font-awesome-icon.ml-2(v-if='isGeneratingExcel', :icon='["fas", "spinner"]', spin)
      .row.mt-3(v-if='excelDownloadError')
        .col.text-danger {{ excelDownloadError }}

      CoachmarkTip.row.mb-2.mt-5.justify-content-between(:options='getCoachmarkData("1")')
        .col-6
          .row()
            .col-6
              select.custom-select(v-model='selectedStatusFilter')
                option(value='') All Workload Completion Statuses
                option(value='Met') Completed
                option(value='Not Met') Not Completed
                option(value='N/A') N/A
            .col-6
              select.custom-select(v-model='selectedRecordFilter')
                option(value='') All Record Statuses
                option(v-for='status in recordStatuses', :value='status.value') {{ status.label }}
        .col-4
          .row.justify-content-end.no-gutters
            .col()
              label.sr-only(for='listFilter') Filter on Name, Department, or Title
              .input-group
                input.form-control#listFilter(type='input', placeholder='Filter on Name, Department, or Section', v-model='searchTerm', :class='searchTerm.length > 0 ? "border-right-0" : ""')
                span.input-group-append(v-if='searchTerm.length > 0')
                  button.btn.border-left-0.border(type='button', @click='searchTerm = ""')
                    span(aria-hidden='true') &times;

      .row.mb-1.align-items-center(v-if='filteredFaculty.length > 0')
        +pagination

      .table-container(ref='tableContainer')
        table.table.table-data.table-striped(v-if='!showErrorMessage', :class='sessionGetDisplayClass("table")')
          thead.sticky-top.has-header#table-header(:class='sessionDisplayIsDark ? "" : "thead-light"')
            CoachmarkTip.pointer(v-for='header in tableHeaders', :key='header.value', @click='changeSort(header.value)', :class='getSortedClass(header.value)', :options='getCoachmarkData(header.coachmark)', elementType='th')
              div(v-html='header.html')
          tbody
            template(v-if='isLoading')
              tr(v-for='i in 10')
                td(v-for='i in 7')
                  VueSkeletonLoader.loading-table-row(:rounded='true')

            template(v-else)
              tr(v-for='person in filteredFacultySlice')
                td
                  router-link.link(:to='getFacultyLink(person.FACULTY_RECORD_UID)', @click.native='scrollToTop') {{ person.EMPLOYEE_NAME }}
                td {{ person.JOB_TYPE }}
                td {{ person.EAC_BRANCH_DESC }}
                td {{ person.EAC_SECTION_DESC }}
                td
                  font-awesome-icon.text-success(v-if='person.WORKLOAD_STATUS === "Met"', :icon='["far", "check-circle"]', title='Completed')
                  font-awesome-icon.text-warning(v-else-if='person.WORKLOAD_STATUS === "Not Met"', :icon='["fas", "exclamation-triangle"]', title='Not Completed')
                  template(v-else) N/A
                td {{ getWauLabel(person) }}
                td {{ getRecordStatusLabel(person.RECORD_STATUS) }}
                  button.btn.p-0.mb-1.ml-1.cursor-default(v-if='person.RECORD_STATUS === "D"', type='button', :title='person.ERRORS ? "Validation errors need to be corrected" : "Ready to submit"')
                    font-awesome-icon(:icon='getValidationIcon(person)', :class='getClassColor(person)')
      .row.mb-3.align-items-center(v-if='filteredFaculty.length > 0')
        +pagination

      .instructions(v-if='!hasSearchedOnce') Select from options above to search for available faculty.
      .no-results(v-if='showNoResultsMessage') No faculty matched the search terms.
      .error-message.text-danger(v-if='!isLoading && showErrorMessage') {{ errorMessage }}
  .container-fluid.text-center(v-else)
    p.text-danger No access, click the button below to go home.
    router-link.btn.btn-wat(to='/home') Home
</template>

<script>
import { mapGetters } from 'vuex';
import { assignIn, every, filter, find, forEach, has, includes, intersectionBy, isEmpty, keys, map, orderBy, slice } from 'lodash';
import CoachmarkTip from '@components/CoachmarkTip';
import Loading from '@components/Loading';
import eventBus from '@api/eventBus';
import resource from '@api/resource';
import watResource from '@api/wat/watResource';
import watUtil from '@api/wat/watUtil';
import VueSkeletonLoader from 'skeleton-loader-vue';

const PARAM_YEAR = 'FACULTY_PARAM_YEAR';
const PARAM_CAMPUS = 'FACULTY_PARAM_CAMPUS';

const _ = { assignIn, every, filter, find, forEach, has, includes, intersectionBy, isEmpty, keys, map, orderBy, slice };

const HTTP_UNAUTHORIZED_STATUS = 401;
const HTTP_FORBIDDEN_STATUS = 403;

const RECORD_STATUSES = {
  D: 'Draft',
  DC: 'Draft (Ready to Submit)',
  DI: 'Draft (Validation Errors)',
  R: 'For Review',
  F: 'Final and Approved'
};

const LABEL_METWORKLOAD = "Completed";
const LABEL_NOTMETWORKLOAD = "Not Completed";
const LABEL_NA = "N/A";

const SORT_ASC = 'asc';
const SORT_DESC = 'desc';

const KEY_TUTORIAL_KEY = 'watList';
const RESULTS_PER_PAGE = 25;

const EXCEL_TEMPLATE_DELAY = 10000; // check for excel every 10 seconds
const EXCEL_TEMPLATE_INTIAL_DELAY = 3000; // wait 3 seconds before checking the first time

const COACHMARKS = [{
  order: '0',
  text: 'Used to select academic year and campus.',
  type: 'watList',
  yPadding: '10'
}, {
  order: '1',
  text: 'The drop-down menus on the left side allows users to filter by Workload Completion Status (i.e., Completed, Not Completed, N/A) and Record Status (i.e., Draft, For Review, Final and Approved).  The search box to the right side of this area can be used to isolate a specific faculty, department, section.',
  type: 'watList',
  yPadding: '10'
}, {
  order: '3',
  text: 'Faculty name as it appears in PeopleSoft (HR).  Click on the name to edit an individual faculty record.',
  type: 'watList',
  yPadding: '10',
  isTransparent: true
}, {
  order: '4',
  text: 'Job type of the faculty member (i.e., Instructional, Research, Specialist).',
  type: 'watList',
  yPadding: '10',
  isTransparent: true
}, {
  order: '5',
  text: 'Department name as it appears in PeopleSoft (HR).  Calculated using EAC (Employing Agency Code).',
  type: 'watList',
  yPadding: '10',
  isTransparent: true
}, {
  order: '6',
  text: 'Section name as it appears in PeopleSoft (HR).  Calculated using EAC (Employing Agency Code).',
  type: 'watList',
  yPadding: '10',
  isTransparent: true
}, {
  order: '7',
  text: 'Workload Completion Status: Calculated using the sum of Instructional Credit Hours and Non-Instructional Equivalencies.  Will be a green checkbox  if faculty members meet their campus’ definition of workload, a yellow triangle with a white exclamation  otherwise (to include those that have not had their workload template reviewed yet).',
  type: 'watList',
  yPadding: '10',
  isTransparent: true
}, {
  order: '8',
  text: 'Total Credit Hours and Equivalencies: Total number of Instructional Credit Hours and Non-Instructional Equivalencies that are a part of their normal workload.',
  type: 'watList',
  yPadding: '10',
  isTransparent: true
}, {
  order: '9',
  text: 'Record Status: Notes the status of the faculty record.',
  type: 'watList',
  yPadding: '10',
  isTransparent: true
}];

export default {
  name: 'FacultyAssignmentList',
  components: {
    CoachmarkTip,
    DueDateBanner: () => import (/* webpackChunkName: "wat_banner" */ '@components/facultyAssignment/DueDateBanner'),
    Loading,
    VueSkeletonLoader
  },
  data: () => ({
    facultyList: [],
    hasSearchedOnce: false,
    isLoading: false,
    searchTerm: '',
    showErrorMessage: false,
    errorMessage: null,
    campusParameters: [{
      value: 'MAN',
      label: 'UH Mānoa'
    }, {
      value: 'HIL',
      label: 'UH Hilo'
    }, {
      value: 'WOA',
      label: 'UH West Oʻahu'
    }, {
      value: 'HAW',
      label: 'Hawaiʻi CC'
    }, {
      value: 'HON',
      label: 'Honolulu CC'
    }, {
      value: 'KAP',
      label: 'Kapiʻolani CC'
    }, {
      value: 'KAU',
      label: 'Kauaʻi CC'
    }, {
      value: 'LEE',
      label: 'Leeward CC'
    }, {
      value: 'MAU',
      label: 'Maui College'
    }, {
      value: 'WIN',
      label: 'Windward CC'
    }],
    tableHeaders: [{
      value: 'EMPLOYEE_NAME',
      html: 'Name',
      coachmark: '3'
    }, {
      value: 'JOB_TYPE',
      html: 'Job Type',
      coachmark: '4'
    }, {
      value: 'EAC_BRANCH_DESC',
      html: 'Department',
      coachmark: '5'
    }, {
      value: 'EAC_BRANCH_SECTION',
      html: 'Section',
      coachmark: '6'
    }, {
      value: 'WORKLOAD_STATUS',
      html: 'Workload Completion Status',
      coachmark: '7'
    }, {
      value: 'WAU',
      html: 'Total Credit Hours and Equivalencies',
      coachmark: '8'
    }, {
      value: 'RECORD_STATUS',
      html: 'Record Status',
      coachmark: '9'
    }],
    selectedCampusParameter: '',
    selectedYearParameter: '',
    selectedStatusFilter: '',
    selectedRecordFilter: '',
    selectedSortedColumn: 'EAC_BRANCH_DESC',
    selectedSortOrder: SORT_ASC,

    currentPage: 1,

    pageLoaded: false,
    isGeneratingExcel: false,
    exportDownloadLink: null,
    excelDownloadError: null
  }),
  computed: {
    ...mapGetters('content', [
      'getCacheForKey',
      'activeWatDates'
    ]),
    ...mapGetters('report', [
      'getSelectedReportFilter'
    ]),
    ...mapGetters('session', [
      'session',
      'sessionDisplayIsDark',
      'sessionWat',
      'sessionGetDisplayClass',
      'sessionHasRole'
    ]),
    canAccessWatTemplates: function () {
      // if they have a template in at least one of these
      return !_.isEmpty(this.sessionWat.canView) || !_.isEmpty(this.sessionWat.canEdit) || !_.isEmpty(this.sessionWat.canFinalize);
    },
    yearParameters: function () {
      return watUtil.YEAR_OPTIONS;
    },
    filteredCampusParameters: function () {
      const availableCampuses = this.sessionWat.campuses ? this.sessionWat.campuses : [];
      return _.intersectionBy(this.campusParameters, availableCampuses, campus => (_.has(campus, 'value') ? campus.value : campus));
    },
    canSearch: function () {
      return !_.isEmpty(this.selectedCampusParameter) && !_.isEmpty(this.selectedYearParameter) && !this.isLoading;
    },
    canGenerateExcel: function () {
      return this.canSearch && !this.isGeneratingExcel;
    },
    showNoResultsMessage: function () {
      // we should have no results, not current loading, able to search, and no error message
      return _.isEmpty(this.filteredFaculty) && !this.isLoading && this.canSearch && !this.showErrorMessage && this.hasSearchedOnce;
    },
    filteredFaculty: function () {
      let toReturn = [];
      if (!_.isEmpty(this.searchTerm) || !_.isEmpty(this.selectedStatusFilter) || !_.isEmpty(this.selectedRecordFilter)) {
        const lowerSearch = this.searchTerm.toLowerCase();
        toReturn = _.filter(this.facultyList, (faculty) => {
          const meetsFilter = _.isEmpty(this.searchTerm) || (faculty.EMPLOYEE_NAME || '').toLowerCase().includes(lowerSearch) || (faculty.EAC_SECTION_DESC || '').toLowerCase().includes(lowerSearch) || (faculty.EAC_BRANCH_DESC || '').toLowerCase().includes(lowerSearch);
          const meetsStatus = _.isEmpty(this.selectedStatusFilter) || faculty.WORKLOAD_STATUS === this.selectedStatusFilter;
          const meetsRecord = _.isEmpty(this.selectedRecordFilter) ||
                              (faculty.RECORD_STATUS === this.selectedRecordFilter) ||
                              (this.selectedRecordFilter === 'DC' && faculty.RECORD_STATUS === 'D' && !faculty.ERRORS) ||
                              (this.selectedRecordFilter === 'DI' && faculty.RECORD_STATUS === 'D' && faculty.ERRORS);

          const meetsCriteria = meetsFilter && meetsStatus && meetsRecord;

          // deselect the record if no longer matching the criteria
          if (!meetsCriteria) {
            faculty.selected = false;
          }
          return meetsCriteria;
        });
      }
      else {
        toReturn = this.facultyList;
      }

      return _.orderBy(toReturn, [this.selectedSortedColumn, 'EMPLOYEE_NAME'], this.selectedSortOrder);
    },
    filteredFacultySlice: function () {
      const startPos = (this.currentPage - 1) * RESULTS_PER_PAGE;
      return _.slice(this.filteredFaculty, startPos, startPos + RESULTS_PER_PAGE);
    },
    recordStatuses: function () {
      return _.map(_.keys(RECORD_STATUSES), value => ({
        value,
        label: value === 'D' ? `${RECORD_STATUSES[value]} (All)` : RECORD_STATUSES[value]
      }));
    },
    numRecordsFound: function () {
      const count = this.filteredFaculty.length;
      if (count > 0) {
        const startPos = ((this.currentPage - 1) * RESULTS_PER_PAGE) + 1;
        return `Showing ${startPos} through ${Math.min(startPos + RESULTS_PER_PAGE - 1, count)} of ${count} record(s)`;
      }
      else {
        return '';
      }
    },
    totalPaginatedPages: function () {
      return Math.ceil(this.filteredFaculty.length / RESULTS_PER_PAGE);
    },
    pagesAvailable: function () {
      const MAX_PAGES = 5;
      const PAGE_NUM_OFFSET = 2;
      let startPage = 1;
      let endPage;

      // if it's in the first set
      if (this.totalPaginatedPages <= MAX_PAGES) {
        endPage = this.totalPaginatedPages;
      }
      else {
        // if it's in the last set
        if (this.currentPage >= this.totalPaginatedPages - PAGE_NUM_OFFSET + 1) {
          startPage = this.totalPaginatedPages - MAX_PAGES + 1;
          endPage = this.totalPaginatedPages;
        }
        else if ((this.currentPage + 2) <= MAX_PAGES) {
          endPage = MAX_PAGES;
        }
        else {
          startPage = this.currentPage - PAGE_NUM_OFFSET;
          endPage = this.currentPage + PAGE_NUM_OFFSET;
        }
      }

      const toReturn = [];
      for (let i = startPage; i <= endPage; i++) {
        toReturn.push(i);
      }
      return toReturn;
    }
  },
  methods: {
    getCoachmarkData: function (order) {
      return _.find(COACHMARKS, { order });
    },
    goToPage: function (newPage) {
      if (newPage >= 1 && newPage <= this.totalPaginatedPages) {
        this.currentPage = newPage;
      }
    },
    changeSort: function (key) {
      if (_.isEmpty(this.filteredFaculty)) {
        return;
      }

      // make it sort ascending if we're changing keys
      if (this.selectedSortedColumn !== key) {
        this.selectedSortOrder = SORT_DESC;
      }
      this.selectedSortedColumn = key;
      this.selectedSortOrder = this.selectedSortOrder === SORT_ASC ? SORT_DESC : SORT_ASC;
    },
    getSortedClass: function (key) {
      if (!_.isEmpty(this.filteredFaculty) && this.selectedSortedColumn === key) {
        return this.selectedSortOrder === SORT_ASC ? 'sort-asc' : 'sort-desc';
      }
    },
    getWorkloadStatusLabel(value){
      if(value === "Met"){
        return LABEL_METWORKLOAD;
      }
      else if(value === "Not Met"){
        return LABEL_NOTMETWORKLOAD;
      }
      else{
        return LABEL_NA;
      }
    },
    getWauLabel: function (person) {
      return `${person.WAU} out of ${person.BASIS}`;
    },
    async doSearch() {
      this.facultyList = []; // clear the results when doing a search
      this.isLoading = true;
      this.hasSearchedOnce = true;
      try {
        const queryParams = {
          YEAR: this.selectedYearParameter,
          IRO_INSTITUTION: this.selectedCampusParameter
        };

        // get the data
        const results = await watResource.get('api/w/facstaff/wat', { queryParams }, null, this.$route.query);

        // save our selected parameters so we can come back to them
        this.$store.dispatch('report/setSelectedFilter', {
          key: PARAM_CAMPUS,
          value: this.selectedCampusParameter
        });
        this.$store.dispatch('report/setSelectedFilter', {
          key: PARAM_YEAR,
          value: this.selectedYearParameter
        });

        // make sure to set the property first so it can be watched in the computed
        _.forEach(results.items, (item) => {
          item.selected = false;
        });

        // add the template ids and roles to our list of sessionWats
        const roles = _.map(results.items, template => ({
          id: template.FACULTY_RECORD_UID,
          role: template.ROLE
        }));
        this.$store.dispatch('session/setSessionWatTemplateRoles', roles);

        this.facultyList = results.items;
      }
      catch (e) {
        console.error(e);
        this.showErrorMessage = true;
        if (e.status === HTTP_UNAUTHORIZED_STATUS) {
          this.errorMessage = 'You must be logged in to view this content.  Please login with the link on the top right.';
        }
        else if (e.status === HTTP_FORBIDDEN_STATUS) {
          this.errorMessage = 'You do not have access.  If you feel this is in error, please contact us.';
        }
        else {
          this.errorMessage = 'Whoops, there was an error loading the content.';
        }
      }
      finally {
        this.isLoading = false;
      }
    },
    getFacultyLink(facultyId) {
      return {
        name: 'facultyAssignment',
        params: {
          facultyId
        },
        query: this.$route.query
      };
    },
    scrollToTop: function () {
      $('html,body').scrollTop(0);
    },
    getRecordStatusLabel: function (value) {
      return RECORD_STATUSES[value] || '';
    },
    getValidationIcon: function (recordObj) {
      return _.has(recordObj, 'ERRORS') ? ['fas', 'exclamation-triangle'] : ['far', 'check-circle'];
    },
    getClassColor: function (recordObj) {
      return _.has(recordObj, 'ERRORS') ? 'text-danger' : 'text-success';
    },
    canSelectRow: function (row) {
      return (row.RECORD_STATUS !== 'D' || !row.ERRORS) && watUtil.canEditRecord(row);
    },
    updateHeader: function (options) {
      this.isHeaderShown = options.isShown;
      const classToAdd = options.hasBanner ? 'has-header-banner' : 'has-header';

      const tableHeader = document.getElementById('table-header');
      if (tableHeader) {
        tableHeader.classList.remove('has-header');
        tableHeader.classList.remove('has-header-banner');
        if (options.isShown) {
          tableHeader.classList.add(classToAdd);
        }
      }
    },
    generateExcelFile: async function () {
      const queryParams = {
        IRO_INSTITUTION: this.selectedCampusParameter,
        YEAR: this.selectedYearParameter,
        ALL_STATUS: true
      };
      this.excelDownloadError = null;
      this.isGeneratingExcel = true;

      try {
        const results = await watResource.get('api/w/facstaff/wat/template', { queryParams }, null, this.$route.query);

        setTimeout(() => {
          this.pingForExcelFile(results.fileName);
        }, EXCEL_TEMPLATE_INTIAL_DELAY);
      }
      catch (err) {
        console.error(err);
        this.isGeneratingExcel = false;
        if (_.has(err, 'body.message')) {
          this.excelDownloadError = err.body.message;
        }
        else {
          this.excelDownloadError = 'There was a problem exporting the data, please try again later.';
        }
      }
    },
    pingForExcelFile: async function (fileName) {
      const pingResults = await watResource.get(`api/w/facstaff/wat/template/${fileName}`, null, null, this.$route.query);

      if (pingResults.data) {
        this.exportDownloadLink = `${resource.apiHost}${resource.BASE_API_PATH}/w/exports/${pingResults.data}`
        this.isGeneratingExcel = false;
      }
      else if (pingResults.error) {
        this.isGeneratingExcel = false;
        this.excelDownloadError = pingResults.error.message || 'There was an error generating the Excel file, please try again';
      }
      else {
        // check again in 10 sec
        setTimeout(() => {
          this.pingForExcelFile(fileName);
        }, EXCEL_TEMPLATE_DELAY);
      }
    },
    filterChange: function () {
      this.exportDownloadLink = null;
    },
  },
  mounted: function () {
    // if only one year and campus default to them and perform the search
    if (this.filteredCampusParameters.length === 1) {
      this.selectedCampusParameter = this.filteredCampusParameters[0].value;
    }
    if (this.yearParameters.length === 1) {
      this.selectedYearParameter = this.yearParameters[0].value;
    }
    if (this.canSearch) {
      this.doSearch();
    }

    // ready to show the support button
    eventBus.bus.$emit(eventBus.CHANNEL_GLOBAL_HELP_BUTTON, {
      type: KEY_TUTORIAL_KEY,
      visible: true
    });

    // show the tutorial
    eventBus.bus.$emit(eventBus.CHANNEL_CLICKED_HELP, {
      type: KEY_TUTORIAL_KEY
    });

    this.updateHeader({
      isShown: true,
      hasBanner: true
    });
  },
  created: function () {
    this.selectedYearParameter = this.getSelectedReportFilter(PARAM_YEAR) || '';
    this.selectedCampusParameter = this.getSelectedReportFilter(PARAM_CAMPUS) || '';
    if (this.canSearch) {
      this.doSearch();
    }

    // add a listener to movement of the header
    eventBus.bus.$on(eventBus.CHANNEL_HEADER_MOVEMENT, this.updateHeader);

    // listen for when records get updated
    eventBus.bus.$on(eventBus.CHANNEL_WAT_RECORD_UPDATED, (value) => {
      const foundTemplate = _.find(this.facultyList, { FACULTY_RECORD_UID: value.FACULTY_RECORD_UID });
      if (foundTemplate) {
        _.assignIn(foundTemplate, value);
      }
    });
  },
  beforeDestroy: function () {
    eventBus.bus.$off(eventBus.CHANNEL_HEADER_MOVEMENT, this.updateHeader);
    eventBus.bus.$off(eventBus.CHANNEL_WAT_RECORD_UPDATED);
  },
  watch: {
    '$route'() {
      // since this page is cached, we want to show the help button when navigating to it the page and hide when going away
      if (this.$route.path === '/templates') {
        eventBus.bus.$emit(eventBus.CHANNEL_GLOBAL_HELP_BUTTON, {
          type: KEY_TUTORIAL_KEY,
          visible: true
        });
      }
      else {
        // hide the support tutorials navigating away from the page
        // if (this.coachmark) {
        //   this.coachmark.destroy();
        // }
        // eventBus.bus.$emit(eventBus.CHANNEL_GLOBAL_HELP_BUTTON, false);
      }
    },
    'totalPaginatedPages'() {
      if (this.totalPaginatedPages > 0 && this.currentPage > this.totalPaginatedPages) {
        this.currentPage = this.totalPaginatedPages;
      }
    }
  },
  updated: function () {
    eventBus.bus.$emit(eventBus.CHANNEL_COACHMARK_UPDATE);
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="stylus" scoped>
  .modal-body
    .nav-link
      color black
      &:hover
        color #007bff !important

  .cursor-default
    cursor default !important

  .pointer
    cursor pointer

  .not-allowed
    cursor not-allowed

  .sort-desc:after,
  .sort-asc:after
    content ' '
    position relative
    left 5px
    border 8px solid transparent

  .sort-desc:after
    top 10px
    border-top-color silver

  .sort-asc:after
    bottom 15px
    border-bottom-color silver

  .sort-desc,
  .sort-asc
    padding-right 10px

  .sticky-top
    transition 0.2s all ease-out
    z-index auto
    top -1px

  .sticky-top.has-header
    top 83px !important
    transition 0.2s all ease-out

  .sticky-top.has-header-banner
    top 133px !important
    transition 0.2s all ease-out

  .page-link
    border 0 !important
    border-radius .25rem !important
    cursor pointer

  .test
    &__mask {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: black;
      opacity: .6;
      transition: opacity 250ms ease-in;
      z-index: 0;
      backdrop-filter: blur(5px);
    }

  .list-top-padding
    padding-top 20px
</style>
